import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type Column,
  DateRenderer,
  EmptyStation,
  type ExplorerDataProvider,
  MessageBar,
  NavigationExplorer,
  sortToPostGraphileOrderBy,
} from '@axinom/mosaic-ui';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { config } from '../../../../config';
import {
  CustomizableServiceDeploymentsDocument,
  type CustomizableServiceDeploymentsQuery,
  type CustomizableServiceDeploymentsQueryVariables,
  ServiceDeploymentsOrderBy,
} from '../../../../generated/hosting-service';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';
import { useServiceDeploymentFilters } from './CustomizableServiceDeployment.filters';
import type { CustomizableServiceDeploymentData } from './CustomizableServiceDeploymentCreate.types';
import {
  ActiveDeploymentRenderer,
  DeploymentStatusRenderer,
} from './ServiceDeploymentStatusRenderer';

export const ImageRenderer = <T extends CustomizableServiceDeploymentData>(
  val: unknown,
  data: T,
): string => {
  if (val) {
    return (
      data.serviceDefinition?.repositoryName + ':' + data.containerImageVersion
    );
  }
  return '';
};

const DeploymentManifestNameRenderer = <
  T extends CustomizableServiceDeploymentData,
>(
  val: unknown,
  data: T,
): string => {
  if (val) {
    return data.serviceDeploymentManifest?.name ?? '';
  }
  return '';
};

const explorerColumns: Column<CustomizableServiceDeploymentData>[] = [
  { label: 'Name', propertyName: 'name' },
  {
    label: 'Container Image',
    propertyName: 'containerImageVersion',
    render: ImageRenderer,
  },
  {
    label: 'Deployment Manifest',
    propertyName: 'serviceDeploymentManifest',
    render: DeploymentManifestNameRenderer,
  },
  {
    label: 'Pilet Artifact(s)',
    propertyName: 'servicePiletArtifacts',
  },
  {
    label: 'Status',
    propertyName: 'status',
    render: DeploymentStatusRenderer,
  },
  {
    label: 'Active Deployment',
    propertyName: 'active',
    render: ActiveDeploymentRenderer,
  },
  {
    label: 'Created At',
    propertyName: 'createdDate',
    render: DateRenderer,
  },
  {
    label: 'Last Modified At',
    propertyName: 'updatedDate',
    render: DateRenderer,
  },
];

export const CustomizableServiceDeploymentExplorer: React.FC = () => {
  const { filterOptions, transformFilters, defaultFilterValues } =
    useServiceDeploymentFilters();

  const { envId, customizableServiceId } = useParams<{
    envId: string;
    customizableServiceId: string;
  }>();

  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  useEffect(() => {
    (async () => {
      const statusMessage = await getManagedServiceAvailabilityMessage(
        ManagedServiceId.HOSTING_SERVICE,
        envId,
      );
      setServiceStatusMessage(statusMessage);
    })();
  }, [envId]);

  const dataProvider: ExplorerDataProvider<CustomizableServiceDeploymentData> =
    {
      loadData: async ({ pagingInformation, sorting, filters }) => {
        const envApolloClient = environmentApolloClient(
          envId,
          new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
        );

        const customizableServiceDeployments = await envApolloClient?.query<
          CustomizableServiceDeploymentsQuery,
          CustomizableServiceDeploymentsQueryVariables
        >({
          query: CustomizableServiceDeploymentsDocument,
          variables: {
            condition: {
              serviceDefinitionId: customizableServiceId,
            },
            filter: transformFilters(filters),
            orderBy: sortToPostGraphileOrderBy(
              sorting,
              ServiceDeploymentsOrderBy,
            ),
            after: pagingInformation,
          },
          fetchPolicy: 'network-only',
        });

        const data = customizableServiceDeployments?.data.filtered?.nodes ?? [];
        return {
          data,
          totalCount:
            customizableServiceDeployments?.data.nonFiltered?.totalCount ?? 0,
          filteredCount:
            customizableServiceDeployments?.data.filtered?.totalCount ?? 0,
          hasMoreData:
            customizableServiceDeployments?.data.filtered?.pageInfo
              .hasNextPage ?? false,
          pagingInformation:
            customizableServiceDeployments?.data.filtered?.pageInfo.endCursor,
        };
      },
    };

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="Customizable Service Deployments">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <NavigationExplorer<CustomizableServiceDeploymentData>
          title="Deployments"
          stationKey="CustomizableServiceDeploymentsExplorer"
          columns={explorerColumns}
          dataProvider={dataProvider}
          defaultFilterValues={defaultFilterValues}
          defaultSortOrder={{ column: 'updatedDate', direction: 'desc' }}
          filterOptions={filterOptions}
          calculateNavigateUrl={(item) => {
            return `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${customizableServiceId}/deployments/${item.id}`;
          }}
          onCreateAction={`/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${customizableServiceId}/deployments/create`}
        />
      )}
    </>
  );
};

export const CustomizableServiceDeploymentExplorerCrumb: BreadcrumbResolver =
  () => 'Deployments';
